import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import "../facility/facility.scss";
import { valueToString } from "helpers/Helpers";

import Page from "components/page/Page";
import Card from "components/card/Card";

import { StatusCode } from "api/protocols";

import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faPaperPlane } from "@fortawesome/pro-solid-svg-icons";
import { GetCustomerType, GetCustomerTypeCounts } from "api/rpc/2024-04/facilityAdmin/customer/type";
import DataTable from "../../../../../components/dataTable/DataTable";
import WelcomeEmailModal from "./components/welcomeEmailModal";
import Portal from "elements/Portal";
import Search from "components/search/Search";
import { TCustomerType } from "redux/reducers/models/facility";
import Spin from "components/spin/spin";

export default function CustomerTypes() {
  const history = useHistory();
  const { t } = useTranslation();
  const [customerTypeState, setCustomerTypeState] = useState<{
    customerTypes: TCustomerType[] | null;
  }>({
    customerTypes: [],
  });

  const [search, setSearch] = useState("");
  const [showWelcomeEmailModal, setShowWelcomeEmailModal] = useState(false);
  const [customerTotals, setCustomerTotals] = useState<Record<number, number> | null>(null);

  useEffect(() => {
    void loadCustomerTypes();

    return () => {
      setCustomerTypeState({
        customerTypes: null,
      });
    };
  }, [search]);

  async function loadCustomerTypes() {
    const res = await GetCustomerType(
      {
        booking_category: true,
        search,
      },
      false,
    );
    if (res.status !== StatusCode.OK) {
      return;
    }

    setCustomerTypeState(prev => ({
      ...prev,
      customerTypes: res.data,
    }));

    await loadCustomerCounts();

  }

  async function loadCustomerCounts() {
    const res = await GetCustomerTypeCounts(false);
    if (res.status !== StatusCode.OK) {
      return;
    }

    const totals: Record<number, number> = {};
    res.data.forEach(count => {
      totals[count.id] = count.customer_collects_count + count.cart_customer_collects_count;
    });
    setCustomerTotals(totals);
  }

  function navigateToNewCustomerType() {
    history.push("/admin/settings/customer_types/new");
  }

  function navigateToEditCustomerType(url: string) {
    history.push(url);
  }

  const primaryAction = {
    content: t("New Customer Type"),
    action: navigateToNewCustomerType,
  };

  function navigateToCustomerRoster(url: string) {
    history.push(url);
  }

  function openWelcomeEmailModal() {
    setShowWelcomeEmailModal(true);
  }

  const secondaryActions = {
    content: (
      <span>
        {<FontAwesomeIcon className="mr-2" icon={faPaperPlane} />}{" "}
        {t("Send Welcome Email")}
      </span>
    ),
    action: openWelcomeEmailModal,
  };

  return (
    <Page
      title={t("Customer Types")}
      subtitle={t("Manage types that can be apply to your customers")}
      primaryAction={primaryAction}
      secondaryActions={[secondaryActions]}
    >
      <div className="mb-4">
        <Search
          historyKey="customer-types-query"
          searchCallback={searchValue => setSearch(searchValue)}
          placeholder={t("Search customer types...")}
        />
      </div>
      <Card>
        <Card.Section table="true">
          <DataTable
            columns={[
              { label: t("Title") },
              { label: t("Subtitle") },
              { label: t("Application") },
              { label: "" },
              { label: t("Total Customers") },
              { label: t("Default Booking Category") },
              { label: "" },
            ]}
            loading={customerTypeState.customerTypes === null ? true : false}
          >
            {customerTypeState.customerTypes?.map((customerType, index: number) => {
              return (
                <tr key={index}>
                  <td
                    className="table-cell-text-lead clickable"
                    onClick={() =>
                      navigateToEditCustomerType("/admin/settings/customer_types/" + String(customerType.id))
                    }
                  >
                    {customerType.title}
                  </td>
                  <td
                    className="table-cell-text-lead clickable"
                    onClick={() =>
                      navigateToEditCustomerType("/admin/settings/customer_types/" + String(customerType.id))
                    }
                  >
                    {customerType.subtitle}
                  </td>
                  <td
                    onClick={() =>
                      navigateToEditCustomerType("/admin/settings/customer_types/" + String(customerType.id))
                    }
                    className="clickable"
                  >
                    <span>{valueToString(customerType?.application ?? '', "capitalize")}</span>
                  </td>
                  <td
                    onClick={() =>
                      navigateToEditCustomerType("/admin/settings/customer_types/" + String(customerType.id))
                    }
                    className="clickable"
                  >
                    {customerType.application === "green_fee" && customerType.color && (
                      <FontAwesomeIcon icon={faCircle} color={customerType?.color} />
                    )}
                  </td>
                  <td
                    className="clickable"
                    onClick={() =>
                      navigateToEditCustomerType("/admin/settings/customer_types/" + String(customerType.id))
                    }
                  >
                    <em>{customerTotals === null ? <div className="size-5 mx-auto"><Spin /></div> : (customerTotals[customerType.id] ?? 0)}</em>
                  </td>
                  <td
                    className="clickable"
                    onClick={() =>
                      navigateToEditCustomerType("/admin/settings/customer_types/" + String(customerType.id))
                    }
                  >
                    {customerType?.default_booking_category?.title}
                  </td>
                  <td className="clickable">
                    <button
                      onClick={() =>
                        navigateToCustomerRoster(
                          "/admin/settings/customer_types/" + String(customerType.id) + "/roster",
                        )
                      }
                    >
                      {t('View Roster')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </DataTable>

          <Portal isMounted={showWelcomeEmailModal}>
            <WelcomeEmailModal
              isOpen={showWelcomeEmailModal}
              customerTypes={customerTypeState?.customerTypes}
              onClose={() => setShowWelcomeEmailModal(false)}
            />
          </Portal>
        </Card.Section>
      </Card>
    </Page>
  );
}
